import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Image, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

export default function ServiceFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = Boolean(id);

    const passedService = location.state?.service;

    const [loading, setLoading] = useState(isEditMode && !passedService);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        title: passedService?.title || "",
        description: passedService?.description || "",
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(
        passedService?.image ? `${process.env.REACT_APP_IMG_URL}/${passedService.image}` : null
    );

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken") ? `Bearer ${localStorage.getItem("authToken")}` : "",
    });

    useEffect(() => {
        if (isEditMode && !passedService) {
            const fetchServiceDetails = async () => {
                try {
                    setLoading(true);
                    const response = await axios.get(`${process.env.REACT_APP_API_URL}/web-manager/services/${id}`, {
                        headers: getAuthHeaders(),
                    });
                    const data = response.data?.data || response.data;
                    setFormData({
                        title: data.title || "",
                        description: data.description || "",
                    });
                    if (data.image) {
                        setImagePreview(`${process.env.REACT_APP_IMG_URL}/${data.image}`);
                    }
                } catch (err) {
                    setError("Failed to fetch service details.");
                } finally {
                    setLoading(false);
                }
            };
            fetchServiceDetails();
        }
    }, [id, isEditMode, passedService]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setErrors((prev) => ({ ...prev, image: "" }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        setErrors({});

        try {
            const data = new FormData();
            data.append("title", formData.title);
            data.append("description", formData.description || "");

            if (imageFile) {
                data.append("image", imageFile);
            }

            if (isEditMode) {
                data.append("_method", "PUT");
                await axios.post(`${process.env.REACT_APP_API_URL}/web-manager/services/${id}`, data, {
                    headers: getAuthHeaders(),
                });
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/web-manager/services`, data, {
                    headers: getAuthHeaders(),
                });
            }

            navigate("/services");
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "An error occurred while saving.");
            }
        } finally {
            setSaving(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
        ],
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2 text-muted">Loading service details...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">{isEditMode ? "Edit Service" : "Create New Service"}</h2>
                        <p className="text-muted mb-0">Fill in title, image, and service description.</p>
                    </Col>
                </Row>

                {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 fw-bold border-0">Service Details</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">
                                            Service Title <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            isInvalid={!!errors.title}
                                            placeholder="Enter service title"
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.title) ? errors.title[0] : errors.title}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">
                                            Service Image
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleImageChange}
                                            isInvalid={!!errors.image}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.image) ? errors.image[0] : errors.image}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    {imagePreview && (
                                        <div className="mt-2">
                                            <Image src={imagePreview} rounded style={{ height: "100px", objectFit: "cover" }} />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 fw-bold border-0">Description</Card.Header>
                        <Card.Body>
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={(content) => setFormData((prev) => ({ ...prev, description: content }))}
                                modules={quillModules}
                            />
                            {errors.description && <div className="text-danger small mt-1">{errors.description[0]}</div>}
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-5">
                        <Button variant="secondary" className="px-4" onClick={() => navigate(-1)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" className="px-5" disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : isEditMode ? "Update Service" : "Create Service"}
                        </Button>
                    </div>
                </Form>
            </Container>
        </PageLayout>
    );
}